import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/constants";
import { HUBSPOT_MEETING_BASE } from "@/lib/meetingLinks";
import axios from "axios";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="payment-success-page">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          {status === "loading" && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto">
                <Loader2 className="w-10 h-10 text-[#00E676] animate-spin" />
              </div>
              <h1 className="text-3xl font-bold">Processing Your Payment...</h1>
              <p className="text-gray-400">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-[#00E676]" />
              </div>
              <h1 className="text-4xl font-bold">Payment Successful!</h1>
              <p className="text-xl text-gray-400">
                Thank you for choosing Digital Media Ninja for your AEO & GEO optimization.
              </p>
              
              {paymentData && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mt-8 text-left">
                  <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
                  <div className="space-y-2 text-gray-400">
                    <p>Amount: <span className="text-white">${(paymentData.amount_total / 100).toFixed(2)}</span></p>
                    {paymentData.metadata?.plan && (
                      <p>Plan: <span className="text-white capitalize">{paymentData.metadata.plan}</span></p>
                    )}
                    <p>Status: <span className="text-[#00E676]">Confirmed</span></p>
                  </div>
                </div>
              )}

              <div className="pt-6 space-y-4">
                <p className="text-gray-400">
                  Book your onboarding call now — or our team will reach out within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href={HUBSPOT_MEETING_BASE} target="_blank" rel="noopener noreferrer" data-testid="payment-success-book-call-btn">
                    <Button className="btn-ninja">
                      <Calendar className="w-4 h-4 mr-2" /> Book Your Onboarding Call
                    </Button>
                  </a>
                  <Link to="/">
                    <Button className="btn-outline">
                      Return Home <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-3xl font-bold">
                {status === "expired" ? "Session Expired" : "Something Went Wrong"}
              </h1>
              <p className="text-gray-400">
                {status === "expired" 
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't confirm your payment. Please contact support if you were charged."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/aeo-geo#pricing">
                  <Button className="btn-ninja">Try Again</Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-outline">Contact Support</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
